import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, FileText, Image as ImageIcon, Video, Download, Trash2, FolderLock, X, Edit2, Eye, Bot, Lock, Info } from 'lucide-react';
import { Skeleton } from '../../../components/ui/skeleton';
import EmptyState from '../../../components/EmptyState';

export default function Vault() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('documents');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    tags: '',
    allowForAI: false,
  });
  const [filterMode, setFilterMode] = useState('all');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    fetchItems();
  }, [activeTab]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const type = activeTab === 'documents' ? 'document' : activeTab === 'media' ? 'media' : null;
      const response = await apiClient.get(`/vault/items${type ? `?type=${type}` : ''}`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch vault items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredItems = () => {
    if (filterMode === 'all') return items;
    if (filterMode === 'ai-enabled') return items.filter(item => item.allowForAI);
    if (filterMode === 'private') return items.filter(item => !item.allowForAI);
    return items;
  };

  const toggleAllowForAI = async (itemId, currentValue) => {
    try {
      await apiClient.put(`/vault/items/${itemId}`, null, {
        params: { allowForAI: !currentValue }
      });
      toast.success(t('success.updated'));
      fetchItems();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.title) {
        setUploadForm({ ...uploadForm, title: file.name });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.title) {
      toast.error(t('errors.uploadFailed'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description || '');
      formData.append('type', activeTab === 'documents' ? 'document' : 'media');
      formData.append('tags', uploadForm.tags);
      formData.append('allowForAI', uploadForm.allowForAI.toString());

      await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('success.uploaded'));
      setUploadForm({ title: '', description: '', tags: '', allowForAI: false });
      setSelectedFile(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      await apiClient.delete(`/vault/items/${itemId}`);
      toast.success(t('success.deleted'));
      fetchItems();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };

  const openRenameDialog = (item) => {
    setItemToRename(item);
    setNewName(item.title);
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    try {
      await apiClient.put(`/vault/items/${itemToRename.id}?title=${encodeURIComponent(newName)}`);
      toast.success(t('success.updated'));
      setRenameDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setUploadForm({ ...uploadForm, title: '' });
  };

  const openPreview = (item) => {
    setPreviewItem(item);
    setPreviewDialogOpen(true);
  };

  const handleDownload = async (item) => {
    try {
      const response = await apiClient.get(`/vault/download/${item.fileId}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('success.uploaded'));
    } catch (error) {
      toast.error(t('errors.loadFailed'));
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6" data-testid="vault-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <FolderLock className="w-8 h-8 text-primary" />
          <h1 className="font-heading text-4xl font-bold text-gray-900">{t('vault.title')}</h1>
        </div>
        <p className="text-lg text-gray-600">
          {t('vault.subtitle')}
        </p>
      </div>

      {/* Vault Description */}
      <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Your Vault is your Twin's Knowledge Source</p>
            <p className="text-blue-800">
              Documents marked <strong>"Allow Twin to Reference"</strong> can be used by your AI Assistant and Skills to provide personalized responses. 
              Toggle this setting per document to control what your Twin can access.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'documents'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="documents-tab"
        >
          {t('vault.documents')}
        </button>
        <button
          onClick={() => setActiveTab('media')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'media'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="media-tab"
        >
          {t('vault.media')}
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'integrations'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Integrations
        </button>
      </div>

      {(activeTab === 'documents' || activeTab === 'media') && (
        <>
          <Card className="p-6 rounded-2xl">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
              {t('vault.uploadFile')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {activeTab === 'documents' 
                ? 'Supported formats: PDF, TXT, DOC, DOCX' 
                : 'Supported formats: JPG, PNG, GIF, MP4, MOV, MP3, WAV'}
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="file-upload" className="block">
                  <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    selectedFile 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-300 hover:border-primary/50 hover:bg-gray-50'
                  }`}>
                    {selectedFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-6 h-6 text-primary" />
                          <span className="font-medium text-gray-900">{selectedFile.name}</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              clearSelectedFile();
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-900">
                          {t('vault.clickToSelect')} {activeTab === 'documents' ? t('vault.document') : t('vault.mediaFile')}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('vault.dragDrop')}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    accept={activeTab === 'documents' ? '.pdf,.txt,.doc,.docx' : 'image/*,video/*,audio/*'}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>

              {selectedFile && (
                <>
                  <div>
                    <Label>{t('vault.title')} *</Label>
                    <Input
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                      placeholder={t('vault.titlePlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('vault.description')}</Label>
                    <Textarea
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                      placeholder={t('vault.descPlaceholder')}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('vault.tags')}</Label>
                    <Input
                      value={uploadForm.tags}
                      onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                      placeholder={t('vault.tagsPlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-blue-600" />
                      <Label htmlFor="allowForAI" className="cursor-pointer text-sm font-medium">
                        {t('vault.allowTwinReference')}
                      </Label>
                    </div>
                    <Switch
                      id="allowForAI"
                      checked={uploadForm.allowForAI}
                      onCheckedChange={(checked) => setUploadForm({ ...uploadForm, allowForAI: checked })}
                    />
                  </div>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || !uploadForm.title}
                    className="w-full"
                    data-testid="upload-file-button"
                  >
                    {uploading ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-pulse" />
                        {t('common.loading')}...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {t('common.upload')}
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </Card>

          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {activeTab === 'documents' ? t('vault.yourDocuments') : t('vault.yourMedia')} ({getFilteredItems().length})
            </h3>
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('vault.allFiles')}</SelectItem>
                <SelectItem value="ai-enabled">{t('vault.aiEnabled')}</SelectItem>
                <SelectItem value="private">{t('vault.private')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div className="flex gap-1">
                      <Skeleton className="w-8 h-8" />
                      <Skeleton className="w-8 h-8" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-5 w-24" />
                  <div className="flex gap-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                  <div className="flex gap-2 pt-3">
                    <Skeleton className="h-9 flex-1" />
                    <Skeleton className="h-9 flex-1" />
                  </div>
                </Card>
              ))}
            </div>
          ) : getFilteredItems().length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {getFilteredItems().map((item) => (
                  <Card key={item.id} className="p-4 rounded-xl hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        {activeTab === 'documents' ? (
                          <FileText className="w-5 h-5 text-primary" />
                        ) : item.mimeType?.startsWith('image') ? (
                          <ImageIcon className="w-5 h-5 text-primary" />
                        ) : (
                          <Video className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRenameDialog(item)}
                        >
                          <Edit2 className="w-4 h-4 text-gray-400 hover:text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                        </Button>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">{item.title}</h4>
                    {item.description && (
                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                    )}
                    
                    {/* AI-Enabled Badge */}
                    <div className="mb-2">
                      {item.allowForAI ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <Bot className="w-3 h-3 mr-1" />
                          {t('vault.aiEnabled')}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          Private
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {item.tags?.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="text-xs text-gray-500 mb-3">
                      {((item.fileSize || 0) / 1024).toFixed(1)} KB • {new Date(item.createdAt).toLocaleDateString()}
                    </div>
                    
                    {/* Allow Twin to Reference Toggle */}
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mb-3">
                      <span className="text-xs font-medium text-gray-700">Allow Twin to Reference</span>
                      <Switch
                        checked={item.allowForAI}
                        onCheckedChange={() => toggleAllowForAI(item.id, item.allowForAI)}
                      />
                    </div>
                    
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openPreview(item)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleDownload(item)}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={activeTab === 'documents' ? FileText : activeTab === 'media' ? ImageIcon : FolderLock}
                title={`No ${activeTab} yet`}
                description={
                  activeTab === 'documents' 
                    ? "Upload documents like PDFs, Word files, or text to store information your Twin can reference."
                    : activeTab === 'media'
                    ? "Upload images, videos, or audio files to enrich your Twin's knowledge base."
                    : "Add training data to help your Twin learn your style and knowledge."
                }
                actionLabel="Upload File"
                onAction={() => setUploadModalOpen(true)}
                secondaryText={activeTab === 'documents' ? "💡 Tip: Documents with AI access can be used by skills" : ""}
              />
            )}
        </>
      )}

      {activeTab === 'integrations' && (
        <Card className="p-12 rounded-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <FolderLock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
            Integrations Coming Soon
          </h3>
          <p className="text-gray-600">
            Connect your calendar, notes, email, and other data sources to enhance your twin's context
          </p>
        </Card>
      )}

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {activeTab === 'documents' ? 'Document' : 'Media'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('placeholders.enterNewName')}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleRename} className="flex-1" disabled={!newName.trim()}>
                Rename
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{previewItem?.title}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {previewItem && (
              <>
                {previewItem.mimeType?.startsWith('image/') ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/vault/preview/${previewItem.fileId}`}
                    alt={previewItem.title}
                    className="w-full h-auto rounded-lg"
                  />
                ) : previewItem.mimeType?.startsWith('video/') ? (
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/vault/preview/${previewItem.fileId}`}
                    controls
                    className="w-full h-auto rounded-lg"
                  />
                ) : previewItem.mimeType?.startsWith('audio/') ? (
                  <div className="p-8 text-center">
                    <audio
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/vault/preview/${previewItem.fileId}`}
                      controls
                      className="w-full"
                    />
                  </div>
                ) : previewItem.mimeType === 'application/pdf' ? (
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/vault/preview/${previewItem.fileId}`}
                    className="w-full h-[500px] rounded-lg"
                    title={previewItem.title}
                  />
                ) : previewItem.mimeType?.includes('text') ? (
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                      Loading preview...
                    </pre>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Preview not available for this file type</p>
                    <Button 
                      onClick={() => handleDownload(previewItem)} 
                      className="mt-4"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
